// ─── Feuille : rattacher une OPÉRATION à une ou PLUSIEURS lignes ──
// Demandé par Cédric le 15/08 (capture de la feuille depuis le compte) :
//   • la barre commune 🔽 · champ · ↕ ;
//   • l'ARBRE budgets → lignes prévues du mois, comme partout (EnTeteArbre) ;
//   • sélection MULTIPLE, avec sur chaque ligne cochée un couple % / montant.
// Le montant reste la source de vérité (règle de la répartition) ; par défaut
// la première ligne cochée reçoit 100 % du reste de l'opération, les
// suivantes partent de zéro — règles testées dans `services/selectionLignes`.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Feuille, CalqueChoix, useUI, useT, frJMA } from '@mop/core';
import { formatEuro, montantOperation, dateEffective } from './services/calculs';
import { occurrencesDuMois } from './services/occurrences';
import { repartitionDepense } from './services/repartition';
import { grouperParBudget, repartirDefauts, cleOcc } from './services/selectionLignes';
import { emojiBudget, emojiCategorie, cleCategorie } from './data/categories';
import { statutDe } from './data/statuts';
import EnTeteArbre, { RETRAIT_ENFANTS } from './EnTeteArbre';
import BarreRecherche from './BarreRecherche';

const libelleMois = (mk, t) => {
  const [y, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk || ''} ${y || ''}`.trim();
};

export default function FeuilleRattacherLignes({
  visible, operation, lignes = [], budgets = [], operations = [],
  exceptions = [], rattachements = [], onValider, onFermer,
  // Les deux AUTRES issues d'un imprévu (reprises de l'ancienne
  // FeuilleRattacher, remplacée le 16/08) : créer la ligne prévue qui
  // manque, ou déclarer un mouvement interne. Optionnelles — le flux des
  // Comptes ne les passe pas.
  onCreerLigne, onInterne,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  // Le mois visé : celui de l'opération. Rattacher mars à avril se fait
  // depuis Suivi, où l'on voit le mois qu'on traite.
  const mk = (dateEffective(operation) || '').slice(0, 7);

  // La sélection, DANS L'ORDRE des cases cochées : l'ordre décide qui reçoit
  // le reste par défaut. `saisie` = montant tapé ; `pct` = % tapé tel quel.
  // ⚠️ `pct: null` = jamais touché (affiche le calculé) ; `''` = VIDÉ par
  // l'utilisateur (affiche vide) — sinon le champ est ineffaçable (16/08).
  const [selection, setSelection] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [sens, setSens] = useState('tout');
  const [tri, setTri] = useState('montant');
  const [panneau, setPanneau] = useState(null);
  const [replies, setReplies] = useState(new Set());

  useEffect(() => {
    if (!visible) return;
    setSelection([]); setRecherche(''); setSens('tout'); setTri('montant');
    setReplies(new Set());
  }, [visible, operation?.id]);

  const occDuMois = useMemo(
    () => (visible && operation
      ? occurrencesDuMois({ lignes, actuals: operations, exceptions, rattachements, mk })
      : []),
    [visible, operation, lignes, operations, exceptions, rattachements, mk],
  );
  const groupes = useMemo(
    () => grouperParBudget(occDuMois, budgets, { recherche, sens, tri }),
    [occDuMois, budgets, recherche, sens, tri],
  );

  const totalOp = Math.abs(operation?.montant || 0);
  // Ce que l'opération peut encore donner : son montant moins les parts déjà
  // rattachées ailleurs.
  const resteInitial = useMemo(
    () => (operation ? repartitionDepense(operation, rattachements).reste : 0),
    [operation, rattachements],
  );
  const parts = useMemo(() => repartirDefauts(selection, resteInitial), [selection, resteInitial]);
  const totalParts = [...parts.values()].reduce((n, v) => n + v, 0);
  const resteApres = Math.round((resteInitial - totalParts) * 100) / 100;

  const selDe = (cle) => selection.find(x => x.cle === cle);
  const basculer = (o) => {
    const cle = cleOcc(o);
    setSelection(prev => (prev.some(x => x.cle === cle)
      ? prev.filter(x => x.cle !== cle)
      : [...prev, { cle, saisie: '', pct: null }]));
  };
  const changerMontant = (cle, v) => setSelection(prev => prev.map(x =>
    (x.cle === cle ? { ...x, saisie: v.replace(/[^0-9.,]/g, ''), pct: null } : x)));
  const changerPct = (cle, v) => {
    const propre = v.replace(/[^0-9.,]/g, '');
    const n = parseFloat(propre.replace(',', '.'));
    setSelection(prev => prev.map(x => (x.cle === cle
      ? { ...x, pct: propre,
          saisie: (totalOp && Number.isFinite(n))
            ? String(Math.round(totalOp * (n / 100) * 100) / 100) : x.saisie }
      : x)));
  };

  // Toutes les lignes cochées doivent porter une part : une part nulle
  // créerait un rattachement vide — le moteur le refuserait après coup.
  const valide = selection.length > 0
    && selection.every(x => (parts.get(x.cle) || 0) > 0.005);

  return (
    <Feuille visible={visible} titre={t('finances.actRattacher')} emoji="🔗" hauteur={0.9}
      sansScroll onFermer={onFermer}
      action={{ icone: '✓', label: t('commun.valider'), disabled: !valide,
        onPress: () => valide && onValider?.(selection.map(x => ({
          occurrence: occDuMois.find(o => cleOcc(o) === x.cle),
          montant: parts.get(x.cle),
        })).filter(p => p.occurrence)) }}>

      {/* Ce qu'on rattache, et ce qu'il RESTE à affecter — recalculé à chaque
          coche : c'est le fil à plomb de la répartition. */}
      {!!operation && (
        <View style={s.source}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.sourceLbl} numberOfLines={1}>
              {operation.libelle || t(cleCategorie(operation.categorie))}
            </Text>
            <Text style={s.sourceSous}>
              {frJMA(dateEffective(operation))}  ·  {montantOperation(operation.montant, operation.sens)}
            </Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={s.resteLbl}>{t('finances.resteAffecter')}</Text>
            <Text style={[s.resteVal, resteApres < -0.005 && { color: UI.danger || '#c0392b' }]}>
              {formatEuro(resteApres)}
            </Text>
          </View>
        </View>
      )}

      {/* `sansScroll` : les blocs fixes portent leur marge latérale. */}
      <View style={{ marginHorizontal: 16 }}>
        <BarreRecherche valeur={recherche} onChange={setRecherche}
          nbFiltres={sens !== 'tout' ? 1 : 0} onFiltres={() => setPanneau('filtres')}
          triActif={tri !== 'montant'} onTri={() => setPanneau('tri')} />
      </View>

      <ScrollView style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 26 }}>
        {/* Le mouvement INTERNE en PREMIÈRE option (Cédric, 16/08) : ni prévu
            ni imprévu — un virement entre ses propres comptes. Le déclarer
            doit être le geste le plus court, pas le plus enfoui. */}
        {!!onInterne && !!operation && (
          <TouchableOpacity style={s.interne} activeOpacity={0.7}
            onPress={() => onInterne(operation, !operation.exclu)}>
            <Text style={s.interneTxt}>
              {operation.exclu ? `↺ ${t('finances.actReintegrer')}` : `⊘ ${t('finances.actInterne')}`}
            </Text>
            <Text style={s.interneAide}>{t('finances.interneAide')}</Text>
          </TouchableOpacity>
        )}

        <Text style={s.titre}>{t('finances.lignesDuMois', { mois: libelleMois(mk, t) })}</Text>
        <Text style={s.aide}>{t('finances.selLignesAide')}</Text>

        {groupes.length === 0 && (
          <Text style={s.vide}>{t('finances.aucuneLigneMois')}</Text>
        )}
        {groupes.map(g => {
          const plie = replies.has(g.cle);
          return (
            <View key={String(g.cle)}>
              <EnTeteArbre plie={plie}
                onBasculer={() => setReplies(prev => {
                  const n = new Set(prev);
                  if (n.has(g.cle)) n.delete(g.cle); else n.add(g.cle);
                  return n;
                })}
                emoji={g.budget ? emojiBudget(g.budget) : '❓'}
                titre={g.budget ? g.budget.nom : t('budget.sansBudget')}
                nb={g.occ.length} />
              {!plie && <View style={{ marginLeft: RETRAIT_ENFANTS }}>
                {g.occ.map(o => {
                  const cle = cleOcc(o);
                  const sel = selDe(cle);
                  const part = parts.get(cle) || 0;
                  const pctCalc = totalOp > 0 ? Math.round((part / totalOp) * 1000) / 10 : 0;
                  return (
                    <View key={cle}>
                      <TouchableOpacity style={[s.item, !!sel && s.itemOn]} activeOpacity={0.7}
                        onPress={() => basculer(o)}>
                        <Text style={s.coche}>{sel ? '☑' : '☐'}</Text>
                        <Text style={{ fontSize: 15 }}>{statutDe(o).emoji}</Text>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text style={[s.itemLbl, !!sel && { fontWeight: '800' }]} numberOfLines={1}>
                            {emojiCategorie(o.ligne?.categorie)} {o.ligne?.libelle || t(cleCategorie(o.ligne?.categorie))}
                          </Text>
                          <Text style={s.itemSous} numberOfLines={1}>
                            {t('finances.reste')} {formatEuro(Math.max(0, Math.abs(o.prevu || 0) - Math.abs(o.reel || 0)))}
                          </Text>
                        </View>
                        <Text style={s.itemMontant}>
                          {montantOperation(o.prevu, o.ligne?.sens)}
                        </Text>
                      </TouchableOpacity>
                      {/* La PART, sous la ligne cochée : montant et % liés
                          dans les deux sens, le montant fait foi. */}
                      {!!sel && (
                        <View style={s.rangeePart}>
                          <TextInput style={[s.input, { flex: 1 }]} value={sel.saisie}
                            onChangeText={(v) => changerMontant(cle, v)}
                            keyboardType="decimal-pad"
                            placeholder={formatEuro(part)} placeholderTextColor={UI.faint} />
                          <View style={s.zonePct}>
                            <TextInput style={s.inputPct}
                              value={sel.pct !== null ? sel.pct : String(pctCalc)}
                              onChangeText={(v) => changerPct(cle, v)}
                              keyboardType="decimal-pad" />
                            <Text style={s.pctSigne}>%</Text>
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>}
            </View>
          );
        })}

        {/* Aucune ligne ne convient : créer celle qui manque, pré-remplie. */}
        {!!onCreerLigne && (
          <TouchableOpacity style={s.creer} activeOpacity={0.7} onPress={onCreerLigne}>
            <Text style={s.creerTxt}>{t('finances.actCreerLigne')}</Text>
          </TouchableOpacity>
        )}
      </ScrollView>

      {/* CALQUES et non <Feuille> : deux <Modal> imbriquées figent l'écran. */}
      <CalqueChoix visible={panneau === 'filtres'} titre={t('finances.filtrer')} emoji="🔽"
        onFermer={() => setPanneau(null)}>
        {/* Le MÊME vocabulaire que la feuille de filtres des comptes. */}
        {[
          { id: 'tout', lbl: t('finances.tousBudgets') },
          { id: 'depense', lbl: `− ${t('finances.sensSorties')}` },
          { id: 'revenu', lbl: `+ ${t('finances.sensEntrees')}` },
        ].map(x => (
          <TouchableOpacity key={x.id} style={[s.item, sens === x.id && s.itemOn]}
            activeOpacity={0.7} onPress={() => { setSens(x.id); setPanneau(null); }}>
            <Text style={s.itemLbl}>{x.lbl}</Text>
            {sens === x.id && <Text style={s.coche}>✓</Text>}
          </TouchableOpacity>
        ))}
      </CalqueChoix>
      <CalqueChoix visible={panneau === 'tri'} titre={t('finances.tri')} emoji="↕"
        onFermer={() => setPanneau(null)}>
        {[
          { id: 'montant', lbl: t('finances.tri_montant') },
          { id: 'nom', lbl: t('finances.tri_libelle') },
        ].map(x => (
          <TouchableOpacity key={x.id} style={[s.item, tri === x.id && s.itemOn]}
            activeOpacity={0.7} onPress={() => { setTri(x.id); setPanneau(null); }}>
            <Text style={s.itemLbl}>{x.lbl}</Text>
            {tri === x.id && <Text style={s.coche}>✓</Text>}
          </TouchableOpacity>
        ))}
      </CalqueChoix>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  source: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, padding: 12, marginHorizontal: 16, marginTop: 16, marginBottom: 10 },
  sourceLbl: { fontSize: 15, fontWeight: '800', color: UI.ink },
  sourceSous: { fontSize: 12.5, color: UI.muted, marginTop: 2, fontWeight: '700' },
  resteLbl: { fontSize: 10.5, color: UI.faint, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.3 },
  resteVal: { fontSize: 16, fontWeight: '800', color: UI.accent, marginTop: 1 },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 6, marginBottom: 2 },
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginBottom: 6 },
  vide: { fontSize: 13, color: UI.faint, lineHeight: 18, marginTop: 8 },
  item: { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, marginBottom: 6 },
  itemOn: { borderColor: UI.accent, borderWidth: 1.5 },
  coche: { fontSize: 16, color: UI.accent, width: 20 },
  itemLbl: { fontSize: 13.5, color: UI.ink, fontWeight: '700' },
  itemSous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  itemMontant: { fontSize: 13, fontWeight: '800', color: UI.ink },
  rangeePart: { flexDirection: 'row', alignItems: 'center', gap: 8, marginLeft: 28, marginBottom: 8 },
  input: { backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 9, paddingHorizontal: 12, fontSize: 15, fontWeight: '800', color: UI.ink },
  zonePct: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12, paddingVertical: 9 },
  inputPct: { minWidth: 46, textAlign: 'right', fontSize: 15, fontWeight: '800', color: UI.ink },
  pctSigne: { fontSize: 14, fontWeight: '800', color: UI.muted },
  creer: { paddingVertical: 12, alignItems: 'center', marginTop: 4 },
  creerTxt: { fontSize: 14, color: UI.accent, fontWeight: '700' },
  interne: { marginBottom: 8, paddingVertical: 13, paddingHorizontal: 13, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card },
  interneTxt: { fontSize: 14, color: UI.ink, fontWeight: '700' },
  interneAide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginTop: 3 },
});
